"use client";
import { useEffect, useState } from "react";
import Toggle from "./toggle";
import { Accordion, AccordionHeader, AccordionBody } from "@material-tailwind/react";
import { hideTags } from "@/lib/api/tag";
import { getLocalizedPath } from "@/components/shared/formatURL";
import { useGenerator } from "@/app/context/GeneratorContext";
import { useTagWarningModal } from "./tag-warning-modal";
import { useGlobalContext } from "@/app/context/store";
export default function ToggleSet({
  currentIndex,
  totalIndex,
  title,
  listOfToggles,
  id,
  type,
  generationType,
  isSelected,
  translations,
  signInModalTranslations,
}: {
  currentIndex: any;
  totalIndex: any;
  title: string;
  listOfToggles: any;
  id: any;
  type: string;
  generationType: string;
  isSelected: boolean;
  translations: any;
  signInModalTranslations: any;
}) {
  const { state, tagListLoading } = useGenerator();
  const { user } = useGlobalContext();
  const [open, setOpen] = useState(1);
  const [editMode, setEditMode] = useState(false); // state for managing edit mode
  const [togglesToHide, setTogglesToHide] = useState<any[]>([]);
  const [allToggles, setAllToggles] = useState<any[]>(listOfToggles);
  const { TagWarningModal, setShowTagWarningModal } = useTagWarningModal(id, translations);

  useEffect(() => {
    setAllToggles(listOfToggles);
  }, [listOfToggles]);

  const handleOpen = (value: any) => {
    setOpen(open === value ? 0 : value);
  };

  const toggleEditMode = async () => {
    if (editMode) {
      // set of "savedTags" ids from tagGroup's listofToggles before removing
      const savedToggleSet = new Set(
        listOfToggles
          .filter((toggle: any) => toggle.savedTag === true)
          .map((toggle: any) => toggle.id),
      );
      const ugcTagsToHide = togglesToHide.filter((toggleId) => savedToggleSet.has(toggleId));
      const hideResult = await hideTags(togglesToHide);

      if (hideResult != null && hideResult != undefined) {
        // hideResult response returns tags removed if default tag
        const tagsRemoved: string[] = hideResult?.response?.data?.hiddenTags || [];
        // If removing ugc tags, push the tags to tagsRemoved
        if (ugcTagsToHide.length > 0) {
          tagsRemoved.push(...ugcTagsToHide);
        }

        // remove tagsRemoved from allToggles
        setAllToggles((prevToggles) =>
          prevToggles.filter((toggle) => !tagsRemoved.includes(toggle.id)),
        );
      }

      setTogglesToHide([]);
    }
    setEditMode(!editMode);
  };

  const handleCancel = () => {
    setEditMode(false);
    setTogglesToHide([]);
  };

  const newURL = getLocalizedPath(
    `tags?group=${id}&style=${state.selectedGenerator.shortName}&browse=true`,
  );

  const handleAddToHide = (tagId: any) => {
    // Add/remove tagId from togglesToHide
    setTogglesToHide((prevState) =>
      prevState.includes(tagId) ? prevState.filter((id) => id !== tagId) : [...prevState, tagId],
    );
  };

  const handleAddTag = () => {
    if (state.customPrompt || state.customNegative || isSelected) {
      setShowTagWarningModal(true);
    } else {
      window.location.href = newURL;
    }
  };

  // @ts-ignore
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    );
  }

  return (
    <>
      <TagWarningModal />
      <div className={`mb-4 ${currentIndex + 1 === totalIndex ? "sm:mb-4" : "sm:mb-0"} shadow-sm`}>
        <Accordion
          open={open === 1}
          icon={<Icon id={1} open={open} />}
          className={`border-[0.5px] border-[#3C3C3C] bg-[#2B2D31] px-2 pt-2 ${
            currentIndex === 0
              ? "sm:rounded-tl-lg sm:rounded-tr-lg"
              : currentIndex + 1 === totalIndex
              ? "sm:rounded-bl-lg sm:rounded-br-lg"
              : ""
          } md:px-4`}
        >
          <AccordionHeader
            onClick={() => handleOpen(1)}
            className={`border-b-0 px-[12px] pb-4 pt-2 text-sm text-white sm:px-0 ${
              open === 1 ? "text-white hover:!text-white" : ""
            }`}
          >
            {tagListLoading ? (
              <>
                <div className="h-4 w-10 animate-pulse rounded-full bg-[#343639] md:block" />
                <div className="ml-2 mr-auto h-4 w-10 animate-pulse rounded-full bg-[#343639] md:block" />
              </>
            ) : (
              <>
                {title}{" "}
                <div className="ml-2 mr-auto text-xs">
                  {type === "single"
                    ? `[${translations.singleSelect}]`
                    : `[${translations.multiSelect}]`}
                </div>
              </>
            )}
          </AccordionHeader>
          <AccordionBody className="pb-2 pt-0 text-base font-normal">
            <div>
              {allToggles.map(function (toggle: any, i: number) {
                return (
                  <Toggle
                    key={i}
                    toggle={toggle}
                    id={id}
                    toggleHide={() => handleAddToHide(toggle.id)}
                    editMode={editMode}
                    togglesToHide={togglesToHide}
                    generationType={generationType}
                    translations={translations}
                    signInModalTranslations={signInModalTranslations}
                  />
                );
              })}
              {generationType === "image" ? (
                <>
                  {" "}
                  {/* Add new button */}
                  {!editMode && user._id !== "none" && (
                    <button
                      type="button"
                      onClick={handleAddTag}
                      style={{
                        backgroundColor: "#343639",
                        opacity: 1,
                      }}
                      className={`mb-2 mr-2 h-8 rounded-full border-[0.5px] border-[#3C3C3C] pl-3.5 pr-3.5 text-sm font-medium text-white hover:bg-gray-900 hover:brightness-105 focus:outline-none focus:ring-0 focus:ring-gray-300 sm:pl-5 sm:pr-5 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700 ${
                        tagListLoading && "animate-pulse border-none"
                      }`}
                    >
                      <div
                        className={`flex h-full flex-row items-center justify-center ${
                          tagListLoading ? "opacity-0" : "opacity-100"
                        }`}
                      >
                        <div>Add New Tags</div>
                      </div>
                    </button>
                  )}{" "}
                  {editMode && (
                    <button
                      type="button"
                      style={{
                        backgroundColor: "#343639",
                      }}
                      onClick={handleCancel}
                      className="mb-2 mr-2 h-8 rounded-full border-[0.5px] border-[#3C3C3C] pl-3.5 pr-3.5 text-sm font-medium text-white hover:bg-gray-900 hover:brightness-105 focus:outline-none focus:ring-0 focus:ring-gray-300 sm:pl-5 sm:pr-5 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700"
                    >
                      <div className="flex h-full flex-row items-center justify-center">
                        <div>Cancel</div>
                      </div>
                    </button>
                  )}
                  {/* Edit Button */}
                  {user._id !== "none" && (
                    <button
                      type="button"
                      style={{
                        backgroundColor: "#343639",
                      }}
                      onClick={() => toggleEditMode()}
                      className={`mb-2 mr-2 h-8 rounded-full border-[0.5px] border-[#3C3C3C] pl-3.5 pr-3.5 text-sm font-medium text-white hover:bg-gray-900 hover:brightness-105 focus:outline-none focus:ring-0 focus:ring-gray-300 sm:pl-5 sm:pr-5 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700 ${
                        tagListLoading && "animate-pulse border-none"
                      }`}
                    >
                      <div
                        className={`flex h-full flex-row items-center justify-center ${
                          tagListLoading ? "opacity-0" : "opacity-100"
                        }`}
                      >
                        {editMode ? "Save" : "Remove Tags"}
                      </div>
                    </button>
                  )}
                </>
              ) : (
                <></>
              )}
            </div>
          </AccordionBody>
        </Accordion>
      </div>
    </>
  );
}
